/**
 * A4 (vague « Fluidité ») — Plans en vigueur au bureau (Documents › Plans en vigueur).
 *
 * Même liste que sur le terrain (GET /plans-en-vigueur : par lot, dernier indice en
 * vigueur, anciens barrés) ; un plan s'ouvre dans la visionneuse annotable partagée
 * (PlanAnnote) : épingles d'actions, toucher / cliquer → Réserve / Action / Photo,
 * filtres, export PDF. Toucher une épingle → la fiche de l'action (ElementDetailDrawer).
 */
import { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { FilePdf, MapPin } from '@phosphor-icons/react';
import PlanAnnote from './PlanAnnote';
import ElementDetailDrawer from '../ElementDetailDrawer';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const dateFr = (iso) => (iso ? new Date(String(iso).length <= 10 ? `${iso}T12:00:00` : iso).toLocaleDateString('fr-FR') : '');
const libelleIndice = (v) => (v?.indice ? `Indice ${v.indice}` : 'Sans indice');

function LignePlan({ plan, onOuvrir }) {
  const v = plan.en_vigueur;
  return (
    <div className="px-4 py-3 border-b border-[#F0F0ED] last:border-b-0" data-testid={`bureau-plan-${plan.cle}`}>
      <div className="flex items-center gap-3">
        <FilePdf size={24} className="text-[#C4773B] shrink-0" />
        <button className="flex-1 min-w-0 text-left" onClick={() => onOuvrir(plan, null)}>
          <p className="text-sm font-semibold text-[#2C2C2C] truncate">{plan.titre}</p>
          <p className="text-xs text-[#9A9A9A]">{v?.uploaded_at ? `Déposé le ${dateFr(v.uploaded_at)}` : ''}{v?.uploaded_by_name ? ` par ${v.uploaded_by_name}` : ''}</p>
        </button>
        <span className="text-xs font-bold text-white bg-[#2D6A4F] px-2 py-1 whitespace-nowrap">En vigueur · {v?.indice ? `ind. ${v.indice}` : 'sans indice'}</span>
        <button onClick={() => onOuvrir(plan, null)} className="text-sm font-semibold text-[#2C2C2C] border border-[#E0E0DB] px-3 py-1.5 hover:border-[#C4773B] inline-flex items-center gap-1"
          data-testid={`bureau-plan-ouvrir-${plan.cle}`}>
          <MapPin size={14} /> Ouvrir et annoter
        </button>
      </div>
      {(plan.anciens || []).length > 0 && (
        <div className="mt-2 ml-9 flex flex-wrap gap-2 items-center">
          <span className="text-xs text-[#9A9A9A]">Anciens indices :</span>
          {plan.anciens.map((a) => (
            <button key={a.document_id} onClick={() => onOuvrir(plan, a.document_id)}
              className="text-xs text-[#9A9A9A] line-through border border-[#E0E0DB] px-2 py-0.5 hover:text-[#6B6B6B]">
              {libelleIndice(a)}{a.uploaded_at ? ` — ${dateFr(a.uploaded_at)}` : ''}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default function PlansEnVigueurBureau({ chantierId }) {
  const [data, setData] = useState(null);
  const [ouvert, setOuvert] = useState(null);
  const [element, setElement] = useState(null);

  useEffect(() => {
    axios.get(`${API_URL}/api/chantiers/${chantierId}/plans-en-vigueur`, { withCredentials: true })
      .then((r) => setData(r.data))
      .catch((e) => { setData({ lots: [], generaux: [] }); toast.error(e.response?.data?.detail || 'Plans indisponibles'); });
  }, [chantierId]);

  const ouvrirAction = useCallback(async (id) => {
    try {
      const { data: e } = await axios.get(`${API_URL}/api/chantiers/${chantierId}/elements/${id}`, { withCredentials: true });
      setElement(e);
    } catch (err) { toast.error(err.response?.data?.detail || 'Action introuvable'); }
  }, [chantierId]);

  if (!data) return <div className="p-8 text-center text-sm text-[#9A9A9A]">Chargement des plans…</div>;
  const sections = [
    ...(data.lots || []).map((s) => ({ cle: s.lot_id, titre: [s.lot_code, s.lot_nom].filter(Boolean).join(' · '), plans: s.plans })),
    ...((data.generaux || []).length ? [{ cle: 'generaux', titre: 'Plans généraux', plans: data.generaux }] : []),
  ];

  return (
    <div data-testid="bureau-plans-en-vigueur">
      <p className="text-sm text-[#6B6B6B] mb-4">
        Le dernier indice de chaque plan est en vigueur ; les anciens indices restent consultables, barrés.
        Ouvrez un plan pour pointer une réserve, une action ou une photo, voir les actions à leur emplacement et exporter le plan annoté en PDF.
      </p>
      {sections.length === 0 && (
        <div className="card-kroquis text-center py-12 text-sm text-[#6B6B6B]">Aucun plan déposé : choisissez la catégorie « Plans » dans la zone de dépôt.</div>
      )}
      <div className="space-y-4">
        {sections.map((s) => (
          <section key={s.cle}>
            <h3 className="text-xs font-bold uppercase tracking-wide text-[#6B6B6B] mb-1.5">{s.titre}</h3>
            <div className="border border-[#E0E0DB] bg-white">
              {s.plans.length === 0
                ? <p className="p-4 text-sm text-[#9A9A9A]">Aucun plan déposé pour ce lot.</p>
                : s.plans.map((p) => <LignePlan key={p.cle} plan={p} onOuvrir={(plan, documentId) => setOuvert({ cle: plan.cle, documentId })} />)}
            </div>
          </section>
        ))}
      </div>
      {ouvert && (
        <PlanAnnote chantierId={chantierId} cle={ouvert.cle} documentId={ouvert.documentId}
          onFermer={() => setOuvert(null)} onOuvrirAction={ouvrirAction} />
      )}
      {element && createPortal(
        <div style={{ position: 'relative', zIndex: 1400 }}>
          <ElementDetailDrawer element={element} chantierId={chantierId} open={!!element}
            onClose={() => setElement(null)} onUpdate={() => ouvrirAction(element._id || element.id)}
            onRemove={() => setElement(null)} />
        </div>,
        document.body,
      )}
    </div>
  );
}
